import { ArrowNarrowLeft, XClose } from '@untitledui/icons'
import { Navigate } from 'react-router-dom'

import StandaloneLoadingSpinner from '@components/StandaloneLoadingSpinner'
import Button from '@components/designSystem/Button'
import Floor from '@components/storeMap/floor/2d/Floor'
import { AnimationProvider } from '@components/storeMap/floor/3d/AnimationProvider'
import StoreMapProvider from '@components/storeMap/provider/Provider'
import { useCurrentStoreId } from '@hooks/useCurrentStore.ts'
import usePersistedState from '@hooks/usePersistedState.ts'
import useTypedTranslation from '@hooks/useTypedTranslation.ts'
import PanoramaLayer from '@routes/Panorama/Layer'
import PanoramaZoomButtons from '@routes/Panorama/PanoramaZoomButtons'
import { usePanoramaNetwork } from '@routes/Panorama/data/panoramaNetwork'
import PanoResetZoomButton from '@routes/Panorama/image/ResetZoomButton'
import { usePanorama } from '@state/panorama.ts'
import { routesFactory } from '@utils/routes'

import { PanoramaImageProvider, usePanoramaContext } from './PanoramaImageContext'
import { ViewingDirectionProvider } from './ViewingDirectionContext'
import { ZoomProvider } from './ZoomContext'
import Image from './image/Image'

function PanoramaRoute() {
  return (
    <PanoramaImageProvider>
      <InvalidStoreKicker />
      <PanoramaRouteContent />
    </PanoramaImageProvider>
  )
}

function PanoramaRouteContent() {
  const { data: panoramaNetwork } = usePanoramaNetwork()
  const { currentImage, resetImage } = usePanoramaContext()

  if (!panoramaNetwork) {
    return <StandaloneLoadingSpinner />
  }

  if (!currentImage) {
    return (
      <div className='absolute inset-0'>
        <PanoramaMap />
        <Hint />
      </div>
    )
  }

  return (
    <ZoomProvider>
      <ViewingDirectionProvider>
        <div className="absolute inset-0">
          <AnimationProvider>
            <Image />
          </AnimationProvider>

          <Button
            variant='outline'
            size='md'
            iconLeading={ArrowNarrowLeft}
            onClick={resetImage}
            aria-label="back-to-map"
            className='absolute top-4 left-4 z-10'
          />

          <MiniMap />

          <PanoResetZoomButton />

          <div className='absolute top-4 right-4'>
            <PanoramaZoomButtons />
          </div>
        </div>
      </ViewingDirectionProvider>
    </ZoomProvider>
  )
}

function MiniMap() {
  return (
    <div className="absolute bottom-4 left-4 z-10 h-50 w-75 rounded-xl border-12 border-white bg-white" data-testid="minimap">
      <PanoramaMap />
    </div>
  )
}

function PanoramaMap() {
  return (
    <StoreMapProvider>
      <Floor
        initialScale={0.8}
        highlightedShelves={[]}
        layerProducers={[
          () => <PanoramaLayer />,
        ]}
      />
    </StoreMapProvider>
  )
}

function Hint() {
  const { t } = useTypedTranslation()
  const [showPanoHint, setShowPanoHint] = usePersistedState({
    key: 'showPanoHint',
    initialState: true,
    storageType: 'session',
  })

  if (!showPanoHint) return null

  return (
    <div className={`
      absolute bottom-20 left-1/2 w-1/4 -translate-x-1/2 rounded-md border border-border-primary bg-white p-2 pr-6
      text-sm font-medium text-gray-600
    `}>

      {t('panorama.hint')}

      <XClose
        size={16}
        onClick={() => setShowPanoHint(false)}
        className='absolute top-1/2 right-2 -translate-y-1/2 cursor-pointer'
      />
    </div>
  )
}

// If there are no panorama images for the store, it doesn't make sense to show the panorama route at all.
// This component checks for that and redirects back to the home page if needed.
function InvalidStoreKicker() {
  const storeId = useCurrentStoreId()
  const { data: panoramaImages } = usePanorama()

  if (!panoramaImages) return null
  if (panoramaImages.length > 0) return null

  return <Navigate to={routesFactory.home(storeId)} />
}

export default PanoramaRoute